import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { BackgroundAtDevice } from '@sites/entity-core';
import { backgroundAtDevice } from '@sites/entity-core';
import type { BlockSpecificForm } from '@sites/entity-signal-form';

import type { NormalizedSpecificBlockEntity } from '@web/types';

import { WidgetDataSubTypes } from '@common/enums';

import { AppFacade } from '@web/store/app/app.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import { WebBlockFormService } from '@web/core/services/web-block-form.service';
import { ImageEditorService } from '@web/shared/modals/image-editor-modal/image-editor.service';

import { SettingsBodyComponent } from '../settings-body/settings-body.component';
import { SettingsBreadcrumbsComponent } from '../settings-breadcrumbs/settings-breadcrumbs.component';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { WebBackgroundImageSettingsComponent } from '@web/shared/components/web-background-image-settings/web-background-image-settings.component';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { BlockLibraryAddonComponent } from '../block-settings/components/block-library-addon/block-library-addon.component';
import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';
import { SpecificBlockMobileSettingsComponent } from './mobile-settings/mobile-settings.component';
import { SpShopCartIconSettingsComponent } from '@libs/components/sp-shop-cart-icon-settings/src/sp-shop-cart-icon-settings.component';

import { AbstractBaseSignalSettingsComponent } from '../abstract-base-settings/abstract-base-signal-settings.component';

/** Which screen of the panel is showing. */
export enum SpecificBlockSettingsScreen {
    Main = 'main',
    Mobile = 'mobile',
    CartIcon = 'cart_icon',
}

/**
 * Settings for a header/footer block. Edits go straight into the block signal
 * form (`WebBlockFormService`), which owns the dispatch and the history entry —
 * this panel only maps controls onto fields.
 */
@Component({
    selector: 'web-specific-block-settings',
    templateUrl: './specific-block-settings.component.html',
    styleUrls: ['./specific-block-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SettingsBodyComponent,
        SettingsBreadcrumbsComponent,
        TooltipModule,
        WebColorPickerComponent,
        WebBackgroundImageSettingsComponent,
        SpSwitchComponent,
        BlockLibraryAddonComponent,
        WebCustomIdComponent,
        SpecificBlockMobileSettingsComponent,
        SpShopCartIconSettingsComponent,
        TranslateModule,
    ],
})
export class SpecificBlockSettingsComponent extends AbstractBaseSignalSettingsComponent implements OnInit {
    public readonly entity = input.required<NormalizedSpecificBlockEntity>();

    private readonly appFacade = inject(AppFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly shopCartIconFacade = inject(ShopCartIconFacade);
    private readonly blockFormService = inject(WebBlockFormService);
    private readonly imageEditorService = inject(ImageEditorService);
    private readonly destroyRef = inject(DestroyRef);

    public form!: BlockSpecificForm;

    public readonly screenEnum = SpecificBlockSettingsScreen;
    public readonly screen = signal<SpecificBlockSettingsScreen>(SpecificBlockSettingsScreen.Main);

    public readonly isAdmin = toSignal(this.appFacade.isAdmin$, { initialValue: false });
    public readonly isDeviceMobile = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    public readonly isCartEnabled = toSignal(this.shopCartIconFacade.isCartEnabled$, { initialValue: false });

    /** Only the page's first block can be pinned to the top, and vice versa. */
    private readonly isFirstBlock = signal(false);
    private readonly isLastBlock = signal(false);

    public ngOnInit(): void {
        this.form = this.blockFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as BlockSpecificForm;

        // The panel opens on the cart icon when the selection came from it.
        this.layoutFacade.selectedEntity$.subscribe((data) => {
            if (data?.entityStep === 'cart_icon') {
                this.screen.set(SpecificBlockSettingsScreen.CartIcon);
            }
        });

        this.blocksFacade.isBlockFirst$(this.entity().id).subscribe((value) => this.isFirstBlock.set(value));
        this.blocksFacade.isBlockLast$(this.entity().id).subscribe((value) => this.isLastBlock.set(value));
    }

    // --- Reads ----------------------------------------------------------------

    // Every read below is a single FieldTree leaf — the v0 form exposes them one
    // by one, so a background edit no longer wakes the pinning readers.

    public readonly background = computed(() => backgroundAtDevice(this.form.background().value(), 'desktop'));
    public readonly backgroundImage = computed(() => this.background().image);

    public readonly backgroundColor = computed<string>(() => {
        const color = this.form.background.color.desktop().value();

        return color ?? '';
    });

    /** Full-bleed is a width of `100vw`; off is the content column, i.e. `null`. */
    public readonly isFullWidth = computed<boolean>(() => this.form.width().value()?.unit === 'vw');
    public readonly hasSeparator = computed<boolean>(() => Boolean(this.form.hasSeparator().value()));
    public readonly pinOnTheTop = computed<boolean>(() => this.form.pin().value() === 'top');
    public readonly pinOnTheBottom = computed<boolean>(() => this.form.pin().value() === 'bottom');

    /** The separator is drawn between rows, so it needs at least two of them. */
    public readonly canHaveSeparator = computed<boolean>(() => (this.entity()?.children?.length ?? 0) >= 2);

    public readonly canPinToTop = computed<boolean>(() => this.isFirstBlock());
    public readonly canPinToBottom = computed<boolean>(() => this.isLastBlock());

    /** Falls back to a slice of the id, which is what the user sees by default. */
    public readonly displayId = computed<string>(
        () => this.entity().meta.displayId || this.entity().id.substring(0, 8)
    );

    // --- Writes ---------------------------------------------------------------

    /**
     * The header has no synchronisation switch of its own — it is on unless an
     * older block persisted it off — so a desktop background edit is mirrored
     * onto the collapsed bar, which is what this panel has always done.
     */
    private get mirroredDevices(): readonly ('desktop' | 'mobile')[] {
        return this.form.syncWithDesktop().value() ? (['desktop', 'mobile'] as const) : (['desktop'] as const);
    }

    /** The header offers the image and its layout only, so nothing else is written. */
    public setBackground({ image, size, position, repeat }: BackgroundAtDevice): void {
        this.form.transact(() => {
            this.mirroredDevices.forEach((device) =>
                this.form.setBackgroundAtDevice({ image, size, position, repeat }, device)
            );
            this.form.compact();
        });
    }

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        const color = presetName ?? value ?? null;

        this.form.transact(() => {
            this.mirroredDevices.forEach((device) => this.form.setBackgroundColor(color, device));
            this.form.compact();
        });
    }

    public setIsFullWidth(value: boolean): void {
        this.form.setWidth(value ? { value: 100, unit: 'vw' } : null);
    }

    public setHasSeparator(value: boolean): void {
        this.form.setHasSeparator(value);
    }

    public setPinOnTheTop(value: boolean): void {
        this.form.setPin(value ? 'top' : null);
    }

    public setPinOnTheBottom(value: boolean): void {
        this.form.setPin(value ? 'bottom' : null);
    }

    public setDisplayId(value: string): void {
        this.form.setDisplayId(value);
    }

    public showScreen(screen: SpecificBlockSettingsScreen): void {
        this.screen.set(screen);
    }

    public showEditImageModal(): void {
        this.imageEditorService.showEditBackgroundImageModal(
            this.backgroundImage() as never,
            this.entity() as never,
            WidgetDataSubTypes.block
        );
    }
}
